import React, { useState, useEffect } from "react";
import axios from "axios";
import { useNavigate, Link } from "react-router-dom";
import { API_BASE_URL } from "./config";

const SignUp = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [message, setMessage] = useState("");
  const [error, setError] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [passwordStrength, setPasswordStrength] = useState("");
  const [passwordMatch, setPasswordMatch] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const navigate = useNavigate();

  // Password strength checker
  useEffect(() => {
    if (password.length === 0) {
      setPasswordStrength("");
      return;
    }

    let strength = 0;
    if (password.length >= 8) strength++;
    if (password.match(/[a-z]/) && password.match(/[A-Z]/)) strength++;
    if (password.match(/[0-9]/)) strength++;
    if (password.match(/[^a-zA-Z0-9]/)) strength++;

    if (strength <= 1) setPasswordStrength("weak");
    else if (strength === 2) setPasswordStrength("medium");
    else if (strength === 3) setPasswordStrength("good");
    else setPasswordStrength("strong");
  }, [password]);

  // Check password match
  useEffect(() => {
    if (confirmPassword.length === 0) {
      setPasswordMatch(null);
      return;
    }
    setPasswordMatch(password === confirmPassword);
  }, [password, confirmPassword]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setMessage("");
    setError(null);

    // Validate password match
    if (password !== confirmPassword) {
      setError("Passwords do not match");
      return;
    }

    // Validate password strength
    if (password.length < 8) {
      setError("Password must be at least 8 characters long");
      return;
    }

    setIsLoading(true);

    try {
      const res = await axios.post(`${API_BASE_URL}/signup`, {
        username: email,
        password,
      });

      if (res.data.message) {
        setMessage(res.data.message);

        if (
          res.data.warning === "email_disabled_auto_verified" ||
          res.data.message.includes("Email verification skipped")
        ) {
          setTimeout(() => navigate("/login"), 2000);
        } else {
          setTimeout(() => navigate("/login"), 3000);
        }
      } else {
        setError("An unexpected error occurred.");
      }
    } catch (err) {
      console.error("Signup error:", err);
      setError(
        err.response?.data?.error ||
          err.message ||
          "Sign up failed. Please try again."
      );
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoogleSignup = () => {
    window.location.href = `${API_BASE_URL}/auth/google`;
  };

  const handleFacebookSignup = () => {
    window.location.href = `${API_BASE_URL}/auth/facebook`;
  };

  const getPasswordStrengthColor = () => {
    switch (passwordStrength) {
      case "weak":
        return "#dc3545";
      case "medium":
        return "#ffc107";
      case "good":
        return "#17a2b8";
      case "strong":
        return "#28a745";
      default:
        return "#e9ecef";
    }
  };

  const getPasswordStrengthWidth = () => {
    switch (passwordStrength) {
      case "weak":
        return "25%";
      case "medium":
        return "50%";
      case "good":
        return "75%";
      case "strong":
        return "100%";
      default:
        return "0%";
    }
  };

  return (
    <div
      className="d-flex align-items-center justify-content-center"
      style={{
        minHeight: "100vh",
        position: "relative",
      }}
    >
      <div
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundImage: "url('/login-signup-background.jpg')",
          backgroundSize: "cover",
          backgroundPosition: "center",
          backgroundRepeat: "no-repeat",
          filter: "blur(3px) brightness(0.8)",
          zIndex: -1,
        }}
      ></div>

      <div
        className="signup-container"
        style={{
          width: "100%",
          maxWidth: "450px",
          padding: "20px",
          position: "relative",
          zIndex: 1,
        }}
      >
        <div className="card shadow">
          <div className="card-body p-4">
            <h2 className="text-center mb-4">Sign Up</h2>

            {/* Social Login Buttons - Temporarily Hidden */}
            {/* <div className="mb-3">
              <button
                type="button"
                className="btn btn-outline-danger w-100 mb-2 d-flex align-items-center justify-content-center"
                onClick={handleGoogleSignup}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="16"
                  height="16"
                  fill="currentColor"
                  className="bi bi-google me-2"
                  viewBox="0 0 16 16"
                >
                  <path d="M15.545 6.558a9.4 9.4 0 0 1 .139 1.626c0 2.434-.87 4.492-2.384 5.885h.002C11.978 15.292 10.158 16 8 16A8 8 0 1 1 8 0a7.7 7.7 0 0 1 5.352 2.082l-2.284 2.284A4.35 4.35 0 0 0 8 3.166c-2.087 0-3.86 1.408-4.492 3.304a4.8 4.8 0 0 0 0 3.063h.003c.635 1.893 2.405 3.301 4.492 3.301 1.078 0 2.004-.276 2.722-.764h-.003a3.7 3.7 0 0 0 1.599-2.431H8v-3.08z" />
                </svg>
                Sign up with Google
              </button>

              <button
                type="button"
                className="btn btn-outline-primary w-100 d-flex align-items-center justify-content-center"
                onClick={handleFacebookSignup}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="16"
                  height="16"
                  fill="currentColor"
                  className="bi bi-facebook me-2"
                  viewBox="0 0 16 16"
                >
                  <path d="M16 8.049c0-4.446-3.582-8.05-8-8.05C3.58 0-.002 3.603-.002 8.05c0 4.017 2.926 7.347 6.75 7.951v-5.625h-2.03V8.05H6.75V6.275c0-2.017 1.195-3.131 3.022-3.131.876 0 1.791.157 1.791.157v1.98h-1.009c-.993 0-1.303.621-1.303 1.258v1.51h2.218l-.354 2.326H9.25V16c3.824-.604 6.75-3.934 6.75-7.951" />
                </svg>
                Sign up with Facebook
              </button>
            </div>

            <div className="text-center mb-3">
              <span className="text-muted">OR</span>
            </div> */}

            {/* Signup Form */}
            <form onSubmit={handleSubmit}>
              <div className="form-group mb-3">
                <label>Email</label>
                <input
                  type="email"
                  className="form-control"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Enter your email"
                  required
                />
              </div>

              <div className="form-group mb-3">
                <label>Password</label>
                <div className="input-group">
                  <input
                    type={showPassword ? "text" : "password"}
                    className="form-control"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Enter your password"
                    required
                    minLength="8"
                  />
                  <button
                    className="btn btn-outline-secondary"
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    <i
                      className={`bi ${
                        showPassword ? "bi-eye-slash" : "bi-eye"
                      }`}
                    ></i>
                  </button>
                </div>

                {/* Password Strength Indicator */}
                {password.length > 0 && (
                  <div className="mt-2">
                    <div className="progress" style={{ height: "5px" }}>
                      <div
                        className="progress-bar"
                        role="progressbar"
                        style={{
                          width: getPasswordStrengthWidth(),
                          backgroundColor: getPasswordStrengthColor(),
                          transition: "all 0.3s ease",
                        }}
                      ></div>
                    </div>
                    <small className="text-muted">
                      Password strength:{" "}
                      <span
                        style={{
                          color: getPasswordStrengthColor(),
                          fontWeight: "bold",
                        }}
                      >
                        {passwordStrength.toUpperCase()}
                      </span>
                    </small>
                  </div>
                )}
                <small className="form-text text-muted">
                  Must be at least 8 characters with uppercase, lowercase, and
                  numbers
                </small>
              </div>

              <div className="form-group mb-3">
                <label>Confirm Password</label>
                <div className="input-group">
                  <input
                    type={showConfirmPassword ? "text" : "password"}
                    className={`form-control ${
                      confirmPassword.length > 0
                        ? passwordMatch
                          ? "is-valid"
                          : "is-invalid"
                        : ""
                    }`}
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    placeholder="Confirm your password"
                    required
                  />
                  <button
                    className="btn btn-outline-secondary"
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  >
                    <i
                      className={`bi ${
                        showConfirmPassword ? "bi-eye-slash" : "bi-eye"
                      }`}
                    ></i>
                  </button>
                </div>
                {confirmPassword.length > 0 && (
                  <small
                    className={passwordMatch ? "text-success" : "text-danger"}
                  >
                    {passwordMatch
                      ? "✓ Passwords match"
                      : "✗ Passwords do not match"}
                  </small>
                )}
              </div>

              <button
                type="submit"
                className="btn btn-primary w-100"
                disabled={isLoading || !passwordMatch}
              >
                {isLoading ? (
                  <>
                    <span
                      className="spinner-border spinner-border-sm me-2"
                      role="status"
                      aria-hidden="true"
                    ></span>
                    Signing up...
                  </>
                ) : (
                  "Sign Up"
                )}
              </button>
            </form>

            {/* Display messages */}
            {message && (
              <div
                className="alert alert-success mt-3 text-center"
                role="alert"
              >
                {message}
              </div>
            )}
            {error && (
              <div className="alert alert-danger mt-3 text-center" role="alert">
                {error}
              </div>
            )}

            <div className="text-center mt-3">
              <small className="text-muted">
                Already have an account? <Link to="/login">Login</Link>
              </small>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SignUp;
